import type { AppraisalDocumentFacade } from "@aprao/model/src/stores/AppraisalDocumentFacade.js";
import type { FinanceFacade } from "@aprao/model/src/stores/FinanceFacade.js";
import type { FlowFacade } from "@aprao/model/src/stores/FlowFacade.js";
import { readOnlyRelativesSetFacadeWithAbsolute } from "@aprao/model/src/stores/RelativesSetFacade.js";
import type { ResidualFacade } from "@aprao/model/src/stores/ResidualFacade.js";
import { makeAutoObservable } from "mobx";

/**
 * A store for the finance metrics using read-only relatives.
 *
 * This store is left here so that we are not tempted to use it in the other parts
 * of the app to derive calculation. It proved itself to be generating loops and hard
 * to debug issues.
 */
export class FinanceSummaryRelatives {
  $stores;

  constructor(stores: {
    appraisal: AppraisalDocumentFacade;
    residual: ResidualFacade;
    finance: FinanceFacade;
    flow: FlowFacade;
  }) {
    makeAutoObservable(this);

    this.$stores = stores;
  }

  get facilitySizeTotal() {
    return readOnlyRelativesSetFacadeWithAbsolute(
      {
        relatives: {
          absolute:
            this.$stores.finance.facilitySizeTotal?.toFraction() ?? null,
          ratioDebtFundedDevCost: null,
          ratioDebtFundedDevAndFinanceCost: null,
          ratioDebtFundedGdv: null,
        },
      },
      {
        ratioDebtFundedDevCost: () =>
          this.$stores.residual.getDevelopmentCostsTotal("debtFunded"),
        ratioDebtFundedDevAndFinanceCost: () =>
          this.$stores.flow.getDevelopmentAndFinanceCostsTotal("debtFunded"),
        ratioDebtFundedGdv: () =>
          this.$stores.appraisal.stages.revenue.getGrossDevelopmentValue(
            "debtFunded",
          ),
      },
    );
  }

  get developerContribution() {
    return readOnlyRelativesSetFacadeWithAbsolute(
      {
        relatives: {
          absolute:
            this.$stores.finance.developerContribution?.toFraction() ?? null,
          ratioDebtFundedDevCost: null,
          ratioDebtFundedDevAndFinanceCost: null,
          ratioDebtFundedGdv: null,
        },
      },
      {
        ratioDebtFundedDevCost: () =>
          this.$stores.residual.getDevelopmentCostsTotal("debtFunded"),
        ratioDebtFundedDevAndFinanceCost: () =>
          this.$stores.flow.getDevelopmentAndFinanceCostsTotal("debtFunded"),
        ratioDebtFundedGdv: () =>
          this.$stores.appraisal.stages.revenue.getGrossDevelopmentValue(
            "debtFunded",
          ),
      },
    );
  }
}
